import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Navigation } from '../components/Navigation';
import { Footer } from '../components/Footer';
import { SideCart } from '../components/SideCart';
import { ChatBot } from '../components/ChatBot';
import { useCart } from '../context/CartContext';
import { ChevronDown } from 'lucide-react';

const categories = [
  {
    key: 'general',
    label: 'General',
    questions: [
      {
        q: 'What are adaptogenic mushrooms?',
        a: 'Adaptogenic mushrooms are fungi that help your body adapt to stress and maintain balance. They contain bioactive compounds — primarily beta-glucans, triterpenes, and polysaccharides — that support immune response, cognitive function, and energy levels.',
      },
      {
        q: 'Are your products vegan and gluten-free?',
        a: 'Yes. All Berleto products are 100% vegan, gluten-free, and made from certified organic ingredients. We never use animal products, gluten-containing fillers, or artificial binders.',
      },
      {
        q: 'How long before I see results?',
        a: 'Most customers report feeling initial benefits within 7 days of daily use. For optimal results, we recommend consistent use for at least 30–60 days as adaptogenic compounds build up in your system over time.',
      },
      {
        q: 'Are your products third-party tested?',
        a: 'Every batch is independently tested by a certified third-party laboratory for purity, potency, and the absence of heavy metals, pesticides, and contaminants. Certificates of Analysis are available on request.',
      },
      {
        q: 'Where are your mushrooms sourced from?',
        a: 'We source exclusively from certified organic farms. Our mushrooms are grown on their natural substrate — wood or grain — and harvested at peak maturity to ensure maximum bioactive compound concentration.',
      },
    ],
  },
  {
    key: 'orders',
    label: 'Orders & Shipping',
    questions: [
      {
        q: 'How do I track my order?',
        a: "Once your order ships, you'll receive an email with a tracking number. Orders typically leave our warehouse within 1–2 business days of being placed.",
      },
      {
        q: 'Do you ship internationally?',
        a: "Currently we ship within the United States only. We're actively working on international expansion — sign up for our newsletter to be notified when we launch in your country.",
      },
      {
        q: 'How long does shipping take?',
        a: 'Standard shipping takes 3–5 business days. We offer free shipping on all orders — no minimum required.',
      },
      {
        q: 'What is your return policy?',
        a: 'We offer a 30-day satisfaction guarantee. If you\'re not completely satisfied, contact us at sales@berleto.com within 30 days of delivery. Opened products may be returned for store credit; unopened items receive a full refund.',
      },
      {
        q: 'Can I modify or cancel my order?',
        a: "Orders can be modified or cancelled within 2 hours of placement. After that, they enter our fulfilment process and cannot be changed. Contact us immediately at sales@berleto.com if you need to make a change.",
      },
    ],
  },
  {
    key: 'usage',
    label: 'Usage',
    questions: [
      {
        q: 'How should I take these supplements?',
        a: 'Take 2 capsules daily with water, preferably with a meal. For best results, take consistently at the same time each day. Morning dosing is most popular, but any time works.',
      },
      {
        q: 'Can I take multiple products together?',
        a: 'Yes. Our products are designed to work synergistically. RUSH and SYNAPTIQ pair well for energy and focus. Adding VITRA provides comprehensive immune and longevity support. Many customers take all three daily.',
      },
      {
        q: 'Are there any side effects?',
        a: "Our products are generally well-tolerated. Some people experience mild digestive changes during the first week as their body adjusts. These typically resolve on their own. If you have specific health concerns or take prescription medications, consult your doctor before starting.",
      },
      {
        q: 'Can I take these supplements while pregnant or breastfeeding?',
        a: 'We recommend consulting your healthcare provider before taking any supplements during pregnancy or breastfeeding. While our ingredients are natural and organic, individual responses can vary.',
      },
      {
        q: 'What happens if I miss a dose?',
        a: "Simply continue with your regular schedule — don't double up. Adaptogens work best with daily, consistent use, but an occasional missed dose will not significantly affect your results.",
      },
    ],
  },
  {
    key: 'subscription',
    label: 'Subscriptions',
    questions: [
      {
        q: 'How does the subscription work?',
        a: 'Subscribe to any product and save 10% on every order. Your chosen products will be automatically shipped on your selected schedule — monthly by default. You can pause, skip, or cancel at any time from your account dashboard.',
      },
      {
        q: 'Can I cancel my subscription anytime?',
        a: 'Yes, no commitment required. You can cancel, pause, or modify your subscription at any time through your account, with no fees or penalties.',
      },
      {
        q: 'When will I be charged?',
        a: 'Your card is charged on the day your order processes, which is the same date each month as your original purchase date. You\'ll receive an email reminder 3 days before each charge.',
      },
    ],
  },
];

function FAQItem({ q, a, index }) {
  const [open, setOpen] = useState(false);

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ delay: index * 0.05, ease: [0.22, 1, 0.36, 1] }}
      className="border-b"
      style={{ borderColor: 'rgba(26,42,26,0.08)' }}
    >
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between py-5 text-left gap-8 group"
      >
        <span
          className="text-sm font-medium transition-colors"
          style={{ color: open ? '#B8704D' : '#1A2A1A' }}
        >
          {q}
        </span>
        <motion.div
          animate={{ rotate: open ? 180 : 0 }}
          transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
          className="flex-shrink-0"
        >
          <ChevronDown className="w-4 h-4" style={{ color: '#B8704D' }} />
        </motion.div>
      </button>

      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
            className="overflow-hidden"
          >
            <p className="pb-5 text-sm leading-relaxed pr-10" style={{ color: 'rgba(26,42,26,0.6)' }}>
              {a}
            </p>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

export function FAQPage() {
  const { isCartOpen, setIsCartOpen, cartItems, updateQuantity, removeItem, cartItemsCount } = useCart();
  const navigate = useNavigate();

  return (
    <div className="min-h-screen overflow-x-hidden" style={{ background: '#FAF9F6' }}>
      <Navigation onCartOpen={() => setIsCartOpen(true)} cartItemsCount={cartItemsCount} />

      {/* Hero */}
      <section className="pt-32 pb-20" style={{ background: '#FAF9F6' }}>
        <div className="container mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-16 items-end">
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
            >
              <p className="text-xs font-medium tracking-[0.3em] uppercase mb-6" style={{ color: '#B8704D' }}>
                Support
              </p>
              <h1 className="text-5xl md:text-6xl font-light leading-tight mb-6" style={{ color: '#1A2A1A' }}>
                Questions,<br />
                <span style={{ color: 'rgba(26,42,26,0.28)' }}>answered.</span>
              </h1>
              <p className="text-sm leading-relaxed" style={{ color: 'rgba(26,42,26,0.5)', maxWidth: '44ch' }}>
                Everything you need to know about our products, shipping, subscriptions, and more. Can't find what you're looking for? Reach out directly.
              </p>
            </motion.div>

            {/* Quick stats */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
              className="grid grid-cols-2 gap-px border"
              style={{ borderColor: 'rgba(26,42,26,0.08)', background: 'rgba(26,42,26,0.08)' }}
            >
              {[
                { n: '24h', label: 'Response time' },
                { n: '30-day', label: 'Return guarantee' },
                { n: 'Free', label: 'USA shipping' },
                { n: '12k+', label: 'Happy customers' },
              ].map((s, i) => (
                <div key={i} className="px-8 py-7" style={{ background: '#FAF9F6' }}>
                  <p className="text-2xl font-light mb-1" style={{ color: '#1A2A1A' }}>{s.n}</p>
                  <p className="text-xs tracking-widest uppercase" style={{ color: 'rgba(26,42,26,0.4)' }}>{s.label}</p>
                </div>
              ))}
            </motion.div>
          </div>
        </div>
      </section>

      {/* FAQ — all categories */}
      <section className="pb-24" style={{ background: '#FAF9F6' }}>
        <div className="container mx-auto px-6">
          <div className="grid md:grid-cols-[180px_1fr] gap-16 items-start">

            {/* Sticky sidebar */}
            <div className="hidden md:flex md:flex-col gap-1 md:sticky md:top-32">
              {categories.map((c) => (
                <a
                  key={c.key}
                  href={`#${c.key}`}
                  className="py-2 text-xs tracking-wide transition-colors"
                  style={{ color: 'rgba(26,42,26,0.45)', paddingLeft: '12px', borderLeft: '1px solid rgba(26,42,26,0.1)' }}
                  onMouseEnter={e => e.currentTarget.style.color = '#B8704D'}
                  onMouseLeave={e => e.currentTarget.style.color = 'rgba(26,42,26,0.45)'}
                >
                  {c.label}
                </a>
              ))}
              <div className="mt-8 pt-8 border-t" style={{ borderColor: 'rgba(26,42,26,0.08)' }}>
                <p className="text-xs mb-3" style={{ color: 'rgba(26,42,26,0.4)', paddingLeft: '12px' }}>Need more help?</p>
                <button
                  onClick={() => navigate('/contact')}
                  className="text-xs font-medium transition-colors"
                  style={{ color: '#B8704D', paddingLeft: '12px' }}
                  onMouseEnter={e => e.currentTarget.style.color = '#a56340'}
                  onMouseLeave={e => e.currentTarget.style.color = '#B8704D'}
                >
                  Contact us →
                </button>
              </div>
            </div>

            {/* All categories */}
            <div className="space-y-16">
              {categories.map((cat) => (
                <div key={cat.key} id={cat.key}>
                  <motion.div
                    initial={{ opacity: 0, y: 16 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
                    className="flex items-center gap-4 mb-6"
                  >
                    <p className="text-xs font-medium tracking-[0.25em] uppercase" style={{ color: '#B8704D' }}>
                      {cat.label}
                    </p>
                    <div className="flex-1 h-px" style={{ background: 'rgba(26,42,26,0.08)' }} />
                  </motion.div>

                  {cat.questions.map((item, i) => (
                    <FAQItem key={i} q={item.q} a={item.a} index={i} />
                  ))}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Still have questions */}
      <section className="py-24" style={{ background: '#1A2A1A' }} data-bubble-theme="light">
        <div className="container mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7 }}
            >
              <p className="text-xs font-medium tracking-[0.3em] uppercase mb-6" style={{ color: '#B8704D' }}>
                Get in touch
              </p>
              <h2 className="text-4xl md:text-5xl font-light mb-4" style={{ color: '#FAF9F6' }}>
                Still have<br />
                <span style={{ color: 'rgba(232,220,196,0.4)' }}>questions?</span>
              </h2>
              <p className="text-sm leading-relaxed mb-10" style={{ color: 'rgba(139,154,139,0.7)' }}>
                Our team responds within 24 hours. No bots, no scripts — just people who know the products inside out.
              </p>
              <button
                onClick={() => navigate('/contact')}
                className="px-10 py-4 text-sm font-medium tracking-wide transition-colors"
                style={{ background: '#FAF9F6', color: '#1A2A1A' }}
                onMouseEnter={e => e.currentTarget.style.background = '#E8DCC4'}
                onMouseLeave={e => e.currentTarget.style.background = '#FAF9F6'}
              >
                Contact Support
              </button>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.7, delay: 0.15 }}
              className="space-y-0 border-t"
              style={{ borderColor: 'rgba(139,154,139,0.15)' }}
            >
              {[
                { label: 'Email', value: 'sales@berleto.com' },
                { label: 'Response time', value: 'Within 24 hours' },
                { label: 'Hours', value: 'Mon – Fri, 9am – 6pm EST' },
              ].map((row, i) => (
                <div
                  key={i}
                  className="flex justify-between items-center py-5 border-b"
                  style={{ borderColor: 'rgba(139,154,139,0.15)' }}
                >
                  <span className="text-xs tracking-widest uppercase" style={{ color: 'rgba(139,154,139,0.5)' }}>{row.label}</span>
                  <span className="text-sm" style={{ color: '#FAF9F6' }}>{row.value}</span>
                </div>
              ))}
            </motion.div>
          </div>
        </div>
      </section>

      <Footer />

      <SideCart
        isOpen={isCartOpen}
        onClose={() => setIsCartOpen(false)}
        cartItems={cartItems}
        updateQuantity={updateQuantity}
        removeItem={removeItem}
      />

      <ChatBot />
    </div>
  );
}
